import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
  Divider,
  Button,
  Stack,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { NotificationPreferences } from '../../types/user';

interface PreferencesFormProps {
  preferences: NotificationPreferences;
  onSave: (preferences: NotificationPreferences) => Promise<void>;
  loading?: boolean;
}

/**
 * PreferencesForm Component
 * Form for managing notification preferences
 */
export const PreferencesForm: React.FC<PreferencesFormProps> = ({
  preferences: initialPreferences,
  onSave,
  loading = false,
}) => {
  const [preferences, setPreferences] = useState(initialPreferences);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggleMaster = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPreferences({
      ...preferences,
      enabled: event.target.checked,
    });
  };

  const handleToggleInApp = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPreferences({
      ...preferences,
      channels: {
        ...preferences.channels,
        inApp: {
          ...preferences.channels.inApp,
          enabled: event.target.checked,
        },
      },
    });
  };

  const handleToggleEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPreferences({
      ...preferences,
      channels: {
        ...preferences.channels,
        email: {
          ...preferences.channels.email,
          enabled: event.target.checked,
        },
      },
    });
  };

  const handleEmailDigestMode = (event: any) => {
    setPreferences({
      ...preferences,
      channels: {
        ...preferences.channels,
        email: {
          ...preferences.channels.email,
          digestMode: event.target.value as 'immediate' | 'hourly' | 'daily' | 'weekly',
        },
      },
    });
  };

  const handleEmailDigestTime = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPreferences({
      ...preferences,
      channels: {
        ...preferences.channels,
        email: {
          ...preferences.channels.email,
          digestTime: event.target.value,
        },
      },
    });
  };

  const handleToggleCategory = (category: keyof NotificationPreferences['categories']) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPreferences({
      ...preferences,
      categories: {
        ...preferences.categories,
        [category]: event.target.checked,
      },
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccess(false);
      setError(null);

      await onSave(preferences);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(preferences) !== JSON.stringify(initialPreferences);

  return (
    <Box>
      {/* Success Message */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Preferences saved successfully!
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Master Toggle */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={preferences.enabled}
                onChange={handleToggleMaster}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="h6">Enable Notifications</Typography>
                <Typography variant="body2" color="text.secondary">
                  Master switch for all notifications
                </Typography>
              </Box>
            }
          />
        </CardContent>
      </Card>

      {/* Channel Preferences */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Notification Channels
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose how you want to receive notifications
          </Typography>

          <FormGroup>
            {/* In-App Notifications */}
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.channels.inApp.enabled}
                  onChange={handleToggleInApp}
                  disabled={!preferences.enabled}
                />
              }
              label="In-App Notifications"
            />

            {/* Email Notifications */}
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.channels.email.enabled}
                  onChange={handleToggleEmail}
                  disabled={!preferences.enabled}
                />
              }
              label="Email Notifications"
            />

            {/* Email Digest Settings */}
            {preferences.channels.email.enabled && (
              <Box sx={{ ml: 4, mt: 2 }}>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Email Frequency</InputLabel>
                  <Select
                    value={preferences.channels.email.digestMode}
                    label="Email Frequency"
                    onChange={handleEmailDigestMode}
                  >
                    <MenuItem value="immediate">Immediate (as they happen)</MenuItem>
                    <MenuItem value="hourly">Hourly Digest</MenuItem>
                    <MenuItem value="daily">Daily Digest</MenuItem>
                    <MenuItem value="weekly">Weekly Digest</MenuItem>
                  </Select>
                </FormControl>

                {(preferences.channels.email.digestMode === 'daily' ||
                  preferences.channels.email.digestMode === 'weekly') && (
                  <TextField
                    fullWidth
                    size="small"
                    type="time"
                    label="Preferred Time"
                    value={preferences.channels.email.digestTime || '09:00'}
                    onChange={handleEmailDigestTime}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              </Box>
            )}
          </FormGroup>
        </CardContent>
      </Card>

      {/* Category Preferences */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Notification Categories
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Customize which types of notifications you want to receive
          </Typography>

          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.categories.authentication}
                  onChange={handleToggleCategory('authentication')}
                  disabled={!preferences.enabled}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Authentication & Account</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Login alerts, password changes, account approvals
                  </Typography>
                </Box>
              }
            />

            <Divider sx={{ my: 1 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={preferences.categories.contentModeration}
                  onChange={handleToggleCategory('contentModeration')}
                  disabled={!preferences.enabled}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Content Moderation</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Content approval, rejection, and flagging
                  </Typography>
                </Box>
              }
            />

            <Divider sx={{ my: 1 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={preferences.categories.socialInteractions}
                  onChange={handleToggleCategory('socialInteractions')}
                  disabled={!preferences.enabled}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Social Interactions</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Comments, mentions, replies, and tags
                  </Typography>
                </Box>
              }
            />

            <Divider sx={{ my: 1 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={preferences.categories.familyTreeUpdates}
                  onChange={handleToggleCategory('familyTreeUpdates')}
                  disabled={!preferences.enabled}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Family Tree Updates</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Person updates, new relationships, documents, and memories
                  </Typography>
                </Box>
              }
            />

            <Divider sx={{ my: 1 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={preferences.categories.researchCollaboration}
                  onChange={handleToggleCategory('researchCollaboration')}
                  disabled={!preferences.enabled}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Research Collaboration</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Research assignments, updates, and completions
                  </Typography>
                </Box>
              }
            />

            <Divider sx={{ my: 1 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={preferences.categories.reminders}
                  onChange={handleToggleCategory('reminders')}
                  disabled={!preferences.enabled}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Reminders</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Birthdays, anniversaries, and important dates
                  </Typography>
                </Box>
              }
            />

            <Divider sx={{ my: 1 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={preferences.categories.systemAnnouncements}
                  onChange={handleToggleCategory('systemAnnouncements')}
                  disabled={!preferences.enabled}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">System Announcements</Typography>
                  <Typography variant="caption" color="text.secondary">
                    System updates, maintenance, and new features
                  </Typography>
                </Box>
              }
            />
          </FormGroup>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges || saving || loading}
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </Stack>
    </Box>
  );
};
